import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { fetchLeaderboard, type ModelPerformance } from "../api/client";
import { StatePill } from "../lib/state";
import { TOOLTIPS } from "../lib/tooltips";
import { HelpTooltip } from "../lib/HelpTooltip";
import { isBackfilledModel } from "../lib/decisions";
import { SymbolDayView } from "./SymbolDayView";

type Tab = "models" | "day";

/**
 * Per-ticker comparison view. Groups every model that's ever traded this
 * symbol by state, with the currently-active ensemble pinned at the top so
 * the user can answer "what's running, and who's their nearest competitor?"
 * without scrolling.
 */

function fmtNum(v: number | undefined, digits = 2) {
  if (v === undefined || v === null || Number.isNaN(v)) return "—";
  return Number(v).toFixed(digits);
}
function fmtPct(v: number | undefined) {
  if (v === undefined || v === null || Number.isNaN(v)) return "—";
  return `${(Number(v) * 100).toFixed(2)}%`;
}
function pnlClass(v: number | undefined) {
  if (v === undefined) return "";
  return v > 0 ? "pos" : v < 0 ? "neg" : "";
}

const COLS: { key: keyof ModelPerformance; label: string; tip: string; format: (v: number | undefined) => string }[] = [
  { key: "rolling_sharpe_20", label: "Sharpe", tip: TOOLTIPS.sharpe, format: (v) => fmtNum(v as number, 3) },
  { key: "cum_return_pct", label: "Cumulative Return", tip: TOOLTIPS.cumulativeReturn, format: (v) => fmtPct(v as number) },
  { key: "hit_rate", label: "Hit rate", tip: TOOLTIPS.hitRate, format: (v) => fmtPct(v as number) },
  { key: "mean_return_per_pred", label: "Mean/pred", tip: TOOLTIPS.meanPerPred, format: (v) => fmtPct(v as number) },
  { key: "total_predictions", label: "N", tip: TOOLTIPS.nPredictions, format: (v) => String(v ?? 0) },
];

function ModelTable({ rows, title, emptyHint }: { rows: ModelPerformance[]; title: string; emptyHint?: string }) {
  if (rows.length === 0) {
    return emptyHint ? (
      <div className="card">
        <h3 style={{ marginTop: 0 }}>{title}</h3>
        <div style={{ color: "#94a3b8" }}>{emptyHint}</div>
      </div>
    ) : null;
  }
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>
        {title} <span style={{ color: "#94a3b8", fontWeight: 400 }}>({rows.length})</span>
      </h3>
      <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Model</th>
            <th>State</th>
            {COLS.map((c) => (
              <th key={String(c.key)} className="num">
                <HelpTooltip text={c.tip}>{c.label}</HelpTooltip>
              </th>
            ))}
            <th>Updated</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.model_id}>
              <td>
                <Link to={`/models/${encodeURIComponent(r.model_id)}`}>
                  <code>{r.model_id}</code>
                </Link>
                {isBackfilledModel(r.model_id) && (
                  <HelpTooltip text="Synthetic row from backfill_observability.py — no per-model attribution.">
                    <span
                      style={{
                        marginLeft: 8,
                        padding: "1px 6px",
                        borderRadius: 3,
                        background: "#1f2937",
                        color: "#fbbf24",
                        fontSize: 10,
                        fontWeight: 600,
                      }}
                    >
                      BACKFILL
                    </span>
                  </HelpTooltip>
                )}
              </td>
              <td><StatePill state={r.state} active={Boolean(r.in_active_ensemble)} /></td>
              {COLS.map((c) => {
                const v = r[c.key] as number | undefined;
                const cls = c.key === "cum_return_pct" || c.key === "mean_return_per_pred" ? pnlClass(v) : "";
                return <td key={String(c.key)} className={`num ${cls}`}>{c.format(v)}</td>;
              })}
              <td style={{ color: "#94a3b8", fontSize: 12 }}>{r.last_updated ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

export function SymbolPage() {
  const { symbol = "" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  // Honor ?tab=day in the URL so deep-links from ModelDetailPage land on
  // the day view without an extra click. Defaults to "models" otherwise.
  const initialTab: Tab = searchParams.get("tab") === "day" ? "day" : "models";
  const [tab, setTabState] = useState<Tab>(initialTab);
  const setTab = (next: Tab) => {
    setTabState(next);
    // Mirror the active tab to the URL so the back button + bookmarks work.
    const params = new URLSearchParams(searchParams);
    if (next === "day") params.set("tab", "day");
    else params.delete("tab");
    setSearchParams(params, { replace: true });
  };
  const { data, isLoading, error } = useQuery({
    queryKey: ["symbol-models", symbol],
    queryFn: () => fetchLeaderboard({ ticker: symbol, limit: 500 }),
    enabled: !!symbol && tab === "models",
  });

  const groups = useMemo(() => {
    const all = data?.models ?? [];
    const active = all
      .filter((r) => r.state === "paper-live" && r.in_active_ensemble)
      .sort((a, b) => Number(b.rolling_sharpe_20 ?? 0) - Number(a.rolling_sharpe_20 ?? 0));
    const live = all
      .filter((r) => r.state === "live")
      .sort((a, b) => Number(b.rolling_sharpe_20 ?? 0) - Number(a.rolling_sharpe_20 ?? 0));
    const paperShadow = all
      .filter((r) => r.state === "paper-live" && !r.in_active_ensemble)
      .sort((a, b) => Number(b.rolling_sharpe_20 ?? 0) - Number(a.rolling_sharpe_20 ?? 0));
    const retired = all
      .filter((r) => r.state && !["paper-live", "live"].includes(String(r.state)))
      .sort((a, b) => Number(b.rolling_sharpe_20 ?? 0) - Number(a.rolling_sharpe_20 ?? 0));
    const other = all
      .filter((r) => !r.state)
      .sort((a, b) => Number(b.rolling_sharpe_20 ?? 0) - Number(a.rolling_sharpe_20 ?? 0));
    return { active, live, paperShadow, retired, other };
  }, [data]);

  if (!symbol) return <div>Specify a symbol in the URL: /symbols/AAPL</div>;

  const tabButton = (id: Tab, label: string) => (
    <button
      onClick={() => setTab(id)}
      style={{
        padding: "6px 14px",
        background: tab === id ? "#1e293b" : "transparent",
        color: tab === id ? "#e2e8f0" : "#94a3b8",
        border: "1px solid #334155",
        borderBottom: tab === id ? "1px solid #1e293b" : "1px solid #334155",
        borderRadius: "4px 4px 0 0",
        marginRight: 4,
        cursor: "pointer",
        fontSize: 13,
      }}
    >
      {label}
    </button>
  );

  return (
    <div>
      <Link to="/" className="backlink">← Leaderboard</Link>
      <h2 style={{ marginTop: 12 }}>
        <code>{symbol}</code>
      </h2>
      <div style={{ marginBottom: -1 }}>
        {tabButton("models", "Models")}
        {tabButton("day", "Day view")}
      </div>
      <div style={{ borderTop: "1px solid #334155", paddingTop: 16 }}>
        {tab === "day" && <SymbolDayView symbol={symbol} />}
        {tab === "models" && (
          <ModelsTab
            symbol={symbol}
            data={data}
            isLoading={isLoading}
            error={error}
            groups={groups}
          />
        )}
      </div>
    </div>
  );
}

// The original "Models" tab content, lifted into its own component so the
// SymbolPage can switch between it and the new day view cleanly.
function ModelsTab({
  symbol,
  data,
  isLoading,
  error,
  groups,
}: {
  symbol: string;
  data: { count: number; models: ModelPerformance[] } | undefined;
  isLoading: boolean;
  error: unknown;
  groups: {
    active: ModelPerformance[];
    live: ModelPerformance[];
    paperShadow: ModelPerformance[];
    retired: ModelPerformance[];
    other: ModelPerformance[];
  };
}) {
  if (isLoading) return <div>Loading {symbol}…</div>;
  if (error) return <div className="card" style={{ color: "#fca5a5" }}>{String(error)}</div>;

  return (
    <div>
      <div style={{ color: "#94a3b8", fontSize: 13, marginBottom: 12 }}>
        all models ({data?.count ?? 0})
      </div>
      <ModelTable
        rows={groups.live}
        title="Live (real money)"
        emptyHint="No models are currently running with real money for this ticker."
      />
      <ModelTable
        rows={groups.active}
        title="Active paper-live ensemble"
        emptyHint="No paper-live ensemble is currently selected for this ticker."
      />
      <ModelTable
        rows={groups.paperShadow}
        title="Other paper-live (not in active ensemble)"
      />
      <ModelTable
        rows={groups.retired}
        title="Retired / other states"
      />
      <ModelTable
        rows={groups.other}
        title="Backfilled or untracked"
      />
    </div>
  );
}
